import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogModule } from '@openng/optimus-ui/dynamicdialog';
import { forkJoin, Subscription, take } from 'rxjs';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { Contact } from 'src/app/model/contacts/contact';
import { Facility } from 'src/app/model/locations/facility';
import { FacilityNotepadEntry } from 'src/app/model/locations/facility-notepad-entry';
import { MonLog } from 'src/app/model/monlogs/mon-log';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MonlogsApiService } from 'src/app/services/api/monlogs-api.service';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { clearFormArray } from 'src/app/core/functions/common-functions';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { MOBILE_MONLOG_SEARCH_CONTAINER } from 'src/app/constants/common.constants';
import {
  SCADA_ALARMS,
  TOWER_LIGHTS,
  TRANSDUCER_ALARM,
} from 'src/app/constants/monlog-constants';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUIModule } from '@openng/optimus-ui/blockui';
import { DatePipe } from '@angular/common';
import { PanelModule } from '@openng/optimus-ui/panel';
import { CardModule } from '@openng/optimus-ui/card';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { LogEntryComponent } from 'src/app/core/components/log-entry.component';

@Component({
  selector: 'app-mc-log-details',
  imports: [
    ProgressSpinnerComponent,
    TranslateModule,
    DynamicDialogModule,
    BlockUIModule,
    DatePipe,
    ReactiveFormsModule,
    PanelModule,
    CardModule,
    CheckboxModule,
    LogEntryComponent,
  ],
  templateUrl: './mc-log-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mc-log-details.component.scss',
})
export class McLogDetailsComponent
  extends DetailsContainer
  implements OnInit, OnDestroy
{
  protected _activeRoute = inject(ActivatedRoute);
  protected _monLogApi = inject(MonlogsApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _fb = inject(FormBuilder);
  protected _breadCrumbService = inject(BreadCrumbBuilderService);
  protected _authApi = inject(AuthApiService);

  activeTabChangedSub?: Subscription;
  breadCrumbSub?: Subscription;
  monLogForm!: FormGroup;

  facility: WritableSignal<Facility | null> = signal(null);
  facilityNotepadEntries: WritableSignal<FacilityNotepadEntry[]> = signal([]);
  monLog: WritableSignal<MonLog | null> = signal(null);
  pageLoading = signal(true);
  isAlarm = signal(false);
  showNotam = signal(false);
  showSeverity = signal(false);
  showGasReleaseType = signal(false);

  canViewEventReview = signal(false);

  facilities: Facility[] = [];
  contacts: Contact[] = [];

  ngOnInit(): void {
    this.buildForm();
    this.initializeMonLog();
    this.setupBreadCrumbSub();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
    if (this.breadCrumbSub) {
      this.breadCrumbSub.unsubscribe();
    }
  }

  buildForm() {
    this.monLogForm = this._fb.group({
      statusSummary: [null],
      logStatus: null,
      logCategory: null,
      reviewer: null,
      reviewCompletedDt: null,
      reviewRequestedDt: null,
      MonLogCallouts: this._fb.array([]),
      typeOfCalls: [null],
      customerOutage: [null],
      calloutRequired: [null],
      gasReleaseType: null,
      commFailSeverity: null,
      workTypes: null,
      notam: null,
      expireDt: null,
      oneCallNumber: null,
      falseAlarm: [null],
      followUpCall: null,
      timeBegan: [null],
      timeOnSite: null,
      ticketNumber: null,
      firstName: null,
      lastName: null,
      street: null,
      phone: null,
      state: null,
      county: null,
      township: null,
      city: null,
      internalOne: null,
      internalTwo: null,
      Facility: this._fb.group({
        facilityName: null,
        facilityType: null,
        facilityUid: null,
        aor: [{ value: null, disabled: true }],
        company: [{ value: null, disabled: true }],
        pipeline: null,
        facilityId: null,
        businessUnit: [null],
      }),
      MonLogContacts: this._fb.array([]),
    });
    this.monLogForm.disable();
  }

  initializeMonLog() {
    const screenName$ = this._translateService.get('MONLOG.SCREEN.LOG_SEARCH');
    forkJoin([screenName$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
    });

    this.getMonLog();
  }

  getMonLog() {
    this._monLogApi
      .getMonLogWithAlarms(this.id)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.monLog.set(response);
          this.initialize();
        },
        error: (error) => {
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'MONLOG.MESSAGES.ERROR.LOAD_MON_LOG',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });

          const index = this._tabService.getActiveIndex();
          this._tabService.closeTab(index);
        },
      });
  }

  initialize() {
    this.facility.set(this.monLog()?.Facility ?? null);
    this.facilityNotepadEntries.set(
      this.monLog()?.Facility?.NotepadEntries ?? []
    );
    this._breadCrumbService.addBreadCrumb({
      label: this.monLog()?.logId ?? '',
      routerLink: null,
    });
    this.authorizeUsers();
    this.updateForm();
  }

  authorizeUsers() {
    this.canViewEventReview.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.CONTROL_ROOM_EVENTS.VIEW_CONTROL_ROOM_EVENT_REVIEW
      )
    );
  }

  updateForm(): void {
    const currentLog = this.monLog();
    this.monLogForm.patchValue({
      statusSummary: currentLog?.statusSummary,
      logStatus: currentLog?.logStatus,
      logCategory: currentLog?.logCategory,
      customerOutage: currentLog?.customerOutage,
      ticketNumber: currentLog?.ticketNumber,
      typeOfCalls: currentLog?.MonLogTypes?.map((t) => t.label),
      workTypes: currentLog?.MonLogWorkTypes?.map((t) => t.label),
      calloutRequired: currentLog?.calloutRequired,
      gasReleaseType: currentLog?.gasReleaseType
        ? currentLog?.gasReleaseType?.name
        : null,
      commFailSeverity: currentLog?.commFailSeverity
        ? currentLog?.commFailSeverity?.name
        : null,
      notam: currentLog?.notam,
      expireDt: currentLog?.expireDt,
      oneCallNumber: currentLog?.oneCallNumber,
      falseAlarm: currentLog?.falseAlarm,
      followUpCall: currentLog?.followUpCall,
      timeBegan: currentLog?.timeBegan,
      timeOnSite: currentLog?.timeOnSite,
      firstName: currentLog?.firstName,
      lastName: currentLog?.lastName,
      street: currentLog?.street,
      phone: currentLog?.phone,
      state: currentLog?.state,
      // county and township are objects and not straight fields. This is because of the selection/ auto update cases
      // these drop downs use.
      // However, for empty forms we want null instead of an object here
      county: currentLog?.county,
      township: currentLog?.township,
      city: currentLog?.city,
      internalOne: currentLog?.internalOne?.fullName,
      internalTwo: currentLog?.internalTwo?.fullName,
    });

    if (currentLog?.MonLogCallouts) {
      const calloutArray = this.monLogForm.get('MonLogCallouts') as FormArray;
      clearFormArray(calloutArray);
      currentLog?.MonLogCallouts.filter(
        (co) => co.dirtyStatus !== DirtyStatus.DELETED
      ).forEach((c) => {
        calloutArray.push(
          this._fb.group({
            id: c.id,
            firstName: c.firstName,
            lastName: c.lastName,
            startDt: c.startDt,
            endDt: c.endDt,
            contactId: c.ContactId,
          })
        );
      });
    }

    if (currentLog?.Facility) {
      this.monLogForm.get('Facility')?.patchValue({
        facilityName: currentLog?.Facility?.facilityName,
        facilityUid: currentLog?.Facility?.facilityUid,
        facilityType: currentLog?.Facility?.facilityType,
        aor: currentLog?.Facility ? currentLog.Facility.AORs[0].name : null,
        company: currentLog?.Facility?.company,
        pipeline: currentLog?.pipeline,
        businessUnit: currentLog?.businessUnit?.name,
      });
    }

    if (currentLog?.MonLogContacts) {
      const contactArray = this.monLogForm.get('MonLogContacts') as FormArray;
      clearFormArray(contactArray);
      currentLog.MonLogContacts.forEach((c) => {
        contactArray.push(
          this._fb.group({
            id: c.id,
            primary: c,
            supervisor: c?.supervisorFullName
              ? {
                  value: c,
                  disabled: true,
                }
              : null,
            manager: c?.managerFullName
              ? {
                  value: c,
                  disabled: true,
                }
              : null,
          })
        );
      });
    }

    this.checkMonLogTypes();
    this.checkScadaAlarm();
    this.monLogForm.disable();
    this.pageLoading.set(false);
  }

  checkMonLogTypes(): void {
    if (
      this.monLog()?.MonLogTypes?.some((type) => type.label === 'Comm Fail')
    ) {
      this.showSeverity.set(true);
    }

    if (
      this.monLog()?.MonLogTypes?.some((type) => type.label === 'Gas Release')
    ) {
      this.showGasReleaseType.set(true);
    }

    if (
      this.monLog()?.MonLogTypes?.some((type) => type.label === 'Tower Lights')
    ) {
      this.showNotam.set(true);
    }
  }

  checkScadaAlarm(): void {
    if (
      this.monLog()?.MonLogTypes?.some(
        (type) =>
          (type &&
            type.label &&
            type.label.toUpperCase() === SCADA_ALARMS.toUpperCase()) ||
          (type &&
            type.label &&
            type.label.toUpperCase() === TOWER_LIGHTS.toUpperCase()) ||
          (type &&
            type.label &&
            type.label.toUpperCase() === TRANSDUCER_ALARM.toUpperCase())
      )
    ) {
      this.isAlarm.set(true);
    }
  }

  doesAnyTypeRequireReview(): boolean {
    return this.monLog()?.MonLogTypes?.some((mlt) => mlt.requiresCRER) ?? false;
  }

  setupBreadCrumbSub(): void {
    this.breadCrumbSub =
      this._breadCrumbService.breadCrumbSelectedEvent.subscribe(
        (breadCrumb) => {
          this._breadCrumbService.removeBreadCrumb(breadCrumb.item);
          const tab = this._tabService.buildNewTab(
            MOBILE_MONLOG_SEARCH_CONTAINER,
            true
          );
          this._tabService.setMobileTab(tab);
        }
      );
  }

  getContactsControls(): any {
    return (this.monLogForm?.get('MonLogContacts') as UntypedFormGroup)[
      'controls'
    ];
  }
}
